import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import IshaChat from '../components/IshaChat';

export default function AnalyticsPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api()
      .get('/auth/analytics')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const cards = [
    { title: 'Total Scans', value: data?.total_scans ?? '—', color: 'primary', icon: 'activity' },
    { title: 'Detections', value: data?.detections ?? '—', color: 'danger', icon: 'alert-circle' },
    { title: 'Accuracy Rate', value: data ? `${data.accuracy_rate}%` : '—', color: 'success', icon: 'check-circle' },
    { title: 'Avg. Confidence', value: data ? `${data.avg_confidence}%` : '—', color: 'info', icon: 'bar-chart-2' },
  ];

  useEffect(() => {
    if (window.feather) window.feather.replace();
  });

  return (
    <>
      <h1 className="h3 mb-3"><strong>Analytics</strong> Dashboard</h1>

      {/* Summary Cards */}
      <div className="row">
        {cards.map((card) => (
          <div key={card.title} className="col-md-3 col-sm-6 mb-3">
            <div className="card">
              <div className="card-body">
                <div className="row">
                  <div className="col mt-0">
                    <h5 className="card-title">{card.title}</h5>
                  </div>
                  <div className="col-auto">
                    <div className={`stat text-${card.color}`}>
                      <i className="align-middle" data-feather={card.icon}></i>
                    </div>
                  </div>
                </div>
                <h1 className="mt-1 mb-3">{loading ? '...' : card.value}</h1>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Placeholder */}
      <div className="row">
        <div className="col-lg-8">
          <div className="card flex-fill w-100">
            <div className="card-header">
              <h5 className="card-title mb-0">Scan History (Last 30 Days)</h5>
            </div>
            <div className="card-body">
              {data?.scan_history?.length > 0 ? (
                <table className="table table-sm my-0">
                  <thead>
                    <tr><th>Date</th><th>Scans</th></tr>
                  </thead>
                  <tbody>
                    {data.scan_history.map((row) => (
                      <tr key={row.scan_date}>
                        <td>{row.scan_date}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
                  <p className="text-muted mb-0">Chart data will appear once scans have been processed.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card flex-fill w-100">
            <div className="card-header">
              <h5 className="card-title mb-0">Detection Breakdown</h5>
            </div>
            <div className="card-body">
              {data?.detection_breakdown?.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {data.detection_breakdown.map((row) => (
                    <li key={row.cancer_type} className="mb-2 d-flex justify-content-between">
                      <span>{row.cancer_type}</span>
                      <strong>{row.count}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
                  <p className="text-muted mb-0">No data yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Recent Activity</h5>
            </div>
            <div className="card-body">
              <table className="table table-hover my-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Cancer Type</th>
                    <th>Result</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recent_activity?.length > 0 ? (
                    data.recent_activity.map((row, i) => (
                      <tr key={i}>
                        <td>{row.created_at_est || '—'}</td>
                        <td><span className="badge bg-secondary">{row.activity_type}</span></td>
                        <td>{row.cancer_type || '—'}</td>
                        <td>
                          {row.scan_result ? (
                            <span className={`badge bg-${row.scan_result === 'Finished' ? 'success' : row.scan_result === 'Failed' ? 'danger' : 'warning'}`}>
                              {row.scan_result}
                            </span>
                          ) : '—'}
                        </td>
                        <td>{row.confidence != null ? `${row.confidence}%` : '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No activity recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <IshaChat />
    </>
  );
}
